import React, { useEffect, useMemo, useState } from "react";
import api, { errMsg } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Building2, UserPlus, Copy, Ban, RotateCcw, Send, ShieldCheck, Search, Loader2, ExternalLink,
} from "lucide-react";

const STATUS_STYLE = {
  active: "bg-[#D1FAE5] text-[#065F46]", pending: "bg-[#FEF3C7] text-[#92400E]",
  suspended: "bg-[#FEE2E2] text-[#991B1B]", revoked: "bg-[#E5E7EB] text-[#374151]",
};
const TABS = ["all", "active", "pending", "suspended", "revoked"];

export default function ExternalAccounts() {
  const { branches } = useAuth();
  const [data, setData] = useState(null);
  const [meta, setMeta] = useState(null);
  const [tab, setTab] = useState("all");
  const [q, setQ] = useState("");
  const [inviteOpen, setInviteOpen] = useState(false);
  const [manage, setManage] = useState(null);
  const [lastLink, setLastLink] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/external-accounts").then(({ data }) => setData(data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); api.get("/external-accounts/meta").then(({ data }) => setMeta(data)).catch(() => {}); }, []);

  const rows = useMemo(() => {
    let items = data?.items || [];
    if (tab !== "all") items = items.filter((r) => r.status === tab);
    if (q.trim()) {
      const s = q.toLowerCase();
      items = items.filter((r) => [r.organization, r.full_name, r.email].filter(Boolean).some((x) => x.toLowerCase().includes(s)));
    }
    return items;
  }, [data, tab, q]);

  const copyLink = (link) => {
    const full = `${window.location.origin}${link}`;
    navigator.clipboard?.writeText(full);
    toast.success("Setup link copied.");
  };

  return (
    <div className="max-w-5xl" data-testid="external-accounts-page">
      <div className="flex items-center justify-between gap-3 mb-1">
        <h1 className="font-display text-2xl font-bold text-[#252221] flex items-center gap-2"><Building2 size={22} /> External accounts</h1>
        <Button onClick={() => { setLastLink(""); setInviteOpen(true); }} className="bg-[#9C1E22] hover:bg-[#861A1D] text-white gap-1.5 rounded-full" data-testid="external-invite-open-btn">
          <UserPlus size={16} /> Invite account
        </Button>
      </div>
      <p className="text-[15px] text-muted-foreground mb-5">Partners, vendors, facilities and professionals with secure, scoped portal access.</p>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        {TABS.map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`external-tab-${t}`}
            className={`h-9 rounded-full px-4 text-[14px] font-medium capitalize transition-colors ${tab === t ? "bg-[#1C4358] text-white" : "bg-[#EEF2F5] text-[#4B5563] hover:bg-[#E1E8ED]"}`}>
            {t}{data?.counts && t !== "all" && data.counts[t] ? ` (${data.counts[t]})` : ""}
          </button>
        ))}
        <div className="relative ml-auto">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="pl-9 h-9 w-56" data-testid="external-search" />
        </div>
      </div>

      {!data && <div className="space-y-3"><Skeleton className="h-20" /><Skeleton className="h-20" /></div>}
      {data && rows.length === 0 && <Card className="p-10 text-center text-muted-foreground border-[#E6EDF2]" data-testid="external-accounts-empty">No accounts yet.</Card>}

      <div className="space-y-2.5">
        {rows.map((r) => (
          <Card key={r.id} className="p-4 border-[#E6EDF2] flex items-center gap-4" data-testid={`external-account-${r.id}`}>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold text-[#252221] truncate">{r.organization || r.full_name}</span>
                <Badge variant="outline" className="text-[11px]">{r.account_type_label}</Badge>
                <Badge className={`text-[11px] capitalize ${STATUS_STYLE[r.status] || ""}`}>{r.status}</Badge>
                {r.has_pending_invite && <Badge variant="outline" className="text-[11px] text-[#92400E]">invite sent</Badge>}
              </div>
              <div className="text-[13px] text-muted-foreground mt-0.5 truncate">
                {r.full_name}{r.email ? ` · ${r.email}` : ""}{r.service_area ? ` · ${r.service_area}` : ""}
              </div>
            </div>
            <Button size="sm" variant="outline" onClick={() => setManage(r)} className="rounded-full" data-testid={`external-manage-${r.id}`}>Manage</Button>
          </Card>
        ))}
      </div>

      {inviteOpen && meta && (
        <InviteDialog open={inviteOpen} onClose={() => setInviteOpen(false)} meta={meta} branches={branches}
          lastLink={lastLink} setLastLink={setLastLink} copyLink={copyLink} onDone={load} saving={saving} setSaving={setSaving} />
      )}
      {manage && meta && (
        <ManageDialog account={manage} meta={meta} branches={branches} onClose={() => setManage(null)} onDone={load} copyLink={copyLink} />
      )}
    </div>
  );
}

function InviteDialog({ open, onClose, meta, branches, lastLink, setLastLink, copyLink, onDone, saving, setSaving }) {
  const [f, setF] = useState({ email: "", full_name: "", account_type: "facility", organization: "", provider_type: "", service_area: "", phone: "", branch_id: "" });
  const set = (k, v) => setF({ ...f, [k]: v });

  const submit = async () => {
    if (!f.email || !f.full_name) { toast.error("Email and contact name are required."); return; }
    setSaving(true);
    try {
      const payload = { ...f };
      if (!payload.branch_id) delete payload.branch_id;
      if (!payload.provider_type) delete payload.provider_type;
      const { data } = await api.post("/external-accounts/invite", payload);
      setLastLink(data.setup_link);
      toast.success("Invitation created.");
      onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid="external-invite-dialog">
        <DialogHeader>
          <DialogTitle>Invite external account</DialogTitle>
          <DialogDescription>They'll get a secure link to set their own password.</DialogDescription>
        </DialogHeader>
        {lastLink ? (
          <div className="space-y-3" data-testid="external-invite-link">
            <div className="rounded-lg bg-[#D1FAE5] text-[#065F46] p-3 text-[14px] flex items-center gap-2"><ShieldCheck size={16} /> Invitation ready. Share this secure setup link:</div>
            <div className="flex items-center gap-2">
              <Input readOnly value={`${window.location.origin}${lastLink}`} className="h-10 text-[13px]" data-testid="external-invite-link-input" />
              <Button onClick={() => copyLink(lastLink)} variant="outline" className="gap-1.5 shrink-0" data-testid="external-invite-copy-btn"><Copy size={15} /> Copy</Button>
            </div>
            <div className="flex justify-end"><Button onClick={onClose} className="bg-[#1C4358] text-white rounded-full">Done</Button></div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-[13px]">Account type</Label>
                <Select value={f.account_type} onValueChange={(v) => set("account_type", v)}>
                  <SelectTrigger className="mt-1 h-10" data-testid="external-invite-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{meta.account_types.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-[13px]">Provider type</Label>
                <Select value={f.provider_type || "none"} onValueChange={(v) => set("provider_type", v === "none" ? "" : v)}>
                  <SelectTrigger className="mt-1 h-10" data-testid="external-invite-provider"><SelectValue placeholder="Optional" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">—</SelectItem>{meta.provider_types.map((p) => <SelectItem key={p} value={p}>{p.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div><Label className="text-[13px]">Organization</Label><Input value={f.organization} onChange={(e) => set("organization", e.target.value)} className="mt-1 h-10" data-testid="external-invite-org" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-[13px]">Contact name *</Label><Input value={f.full_name} onChange={(e) => set("full_name", e.target.value)} className="mt-1 h-10" data-testid="external-invite-name" /></div>
              <div><Label className="text-[13px]">Email *</Label><Input type="email" value={f.email} onChange={(e) => set("email", e.target.value)} className="mt-1 h-10" data-testid="external-invite-email" /></div>
              <div><Label className="text-[13px]">Phone</Label><Input value={f.phone} onChange={(e) => set("phone", e.target.value)} className="mt-1 h-10" data-testid="external-invite-phone" /></div>
              <div><Label className="text-[13px]">Service area</Label><Input value={f.service_area} onChange={(e) => set("service_area", e.target.value)} className="mt-1 h-10" data-testid="external-invite-area" /></div>
            </div>
            {branches?.length > 0 && (
              <div><Label className="text-[13px]">Branch / territory</Label>
                <Select value={f.branch_id || "none"} onValueChange={(v) => set("branch_id", v === "none" ? "" : v)}>
                  <SelectTrigger className="mt-1 h-10" data-testid="external-invite-branch"><SelectValue placeholder="Optional" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">—</SelectItem>{branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="ghost" onClick={onClose} className="rounded-full">Cancel</Button>
              <Button onClick={submit} disabled={saving} className="bg-[#9C1E22] text-white rounded-full gap-1.5" data-testid="external-invite-submit-btn">
                {saving ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} Send invite
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ManageDialog({ account, meta, branches, onClose, onDone, copyLink }) {
  const [a, setA] = useState(account);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setA({ ...a, [k]: v });

  const act = async (path) => {
    setBusy(true);
    try { const { data } = await api.post(`/external-accounts/${a.id}/${path}`); if (data.status) setA({ ...a, status: data.status }); toast.success("Updated."); onDone(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const resend = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/external-accounts/${a.id}/resend-invite`); copyLink(data.setup_link); onDone(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/external-accounts/${a.id}`, {
        full_name: a.full_name, organization: a.organization, account_type: a.account_type,
        phone: a.phone, service_area: a.service_area, provider_type: a.provider_type || null,
      });
      toast.success("Saved."); onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid="external-manage-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">{a.organization || a.full_name}
            <Badge className={`text-[11px] capitalize ${STATUS_STYLE[a.status] || ""}`}>{a.status}</Badge></DialogTitle>
          <DialogDescription>{a.email} · {a.account_type_label}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-[13px]">Organization</Label><Input value={a.organization || ""} onChange={(e) => set("organization", e.target.value)} className="mt-1 h-10" data-testid="external-manage-org" /></div>
            <div><Label className="text-[13px]">Contact name</Label><Input value={a.full_name || ""} onChange={(e) => set("full_name", e.target.value)} className="mt-1 h-10" /></div>
            <div><Label className="text-[13px]">Account type</Label>
              <Select value={a.account_type} onValueChange={(v) => set("account_type", v)}>
                <SelectTrigger className="mt-1 h-10" data-testid="external-manage-type"><SelectValue /></SelectTrigger>
                <SelectContent>{meta.account_types.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-[13px]">Phone</Label><Input value={a.phone || ""} onChange={(e) => set("phone", e.target.value)} className="mt-1 h-10" /></div>
            <div><Label className="text-[13px]">Service area</Label><Input value={a.service_area || ""} onChange={(e) => set("service_area", e.target.value)} className="mt-1 h-10" /></div>
          </div>
          <div className="flex justify-end"><Button size="sm" onClick={save} disabled={busy} className="bg-[#1C4358] text-white rounded-full" data-testid="external-manage-save">Save details</Button></div>

          <div className="border-t border-[#EEF2F5] pt-3 flex flex-wrap gap-2">
            {a.has_pending_invite && <Button size="sm" variant="outline" onClick={resend} disabled={busy} className="gap-1.5 rounded-full" data-testid="external-manage-resend"><Send size={14} /> Resend invite</Button>}
            {a.status !== "suspended" && a.status !== "revoked" && <Button size="sm" variant="outline" onClick={() => act("suspend")} disabled={busy} className="gap-1.5 rounded-full text-[#92400E]" data-testid="external-manage-suspend"><Ban size={14} /> Suspend</Button>}
            {(a.status === "suspended" || a.status === "revoked") && <Button size="sm" variant="outline" onClick={() => act("reactivate")} disabled={busy} className="gap-1.5 rounded-full text-[#065F46]" data-testid="external-manage-reactivate"><RotateCcw size={14} /> Reactivate</Button>}
            {a.status !== "revoked" && <Button size="sm" variant="ghost" onClick={() => act("revoke")} disabled={busy} className="gap-1.5 rounded-full text-[#9C1E22]" data-testid="external-manage-revoke"><Ban size={14} /> Revoke access</Button>}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
